import React, { useState, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiEye, FiEyeOff, FiUser, FiLock, FiSliders, FiCpu } from 'react-icons/fi';
import { useTranslation, Trans } from 'react-i18next';
import BASE_URL from '../../config/config';
import FotoPerfil from '../../components/ui/FotoPerfil';
import Pestanas from '../../components/ui/Pestanas';
import IntegracionesIA from './perfil/IntegracionesIA';
import SelectorIdioma from '../../components/ui/SelectorIdioma';
import { resolverIdioma } from '../../i18n/idiomas';
import { sincronizarIdiomaConSesion, cambiarIdioma } from '../../i18n';
import { normalizarTelefonoInternacional, validarTelefonoInternacional } from '../../utils/telefono';
import '../../css/perfil.css';
import { cerrarSesion } from '../../auth/sesion';

const Perfil = () => {
  const token = localStorage.getItem('AuthToken');
  const tokenInfo = token ? jwtDecode(token) : {};
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Valores de partida: lo que había guardado al entrar. Se congelan en el
  // montaje y solo se mueven al guardar, porque son la referencia contra la que
  // se decide qué está pendiente. En concreto el idioma NO puede recalcularse:
  // `resolverIdioma()` lee el localStorage que el propio selector acaba de
  // escribir al pulsar, así que la diferencia se anulaba sola y el cambio no
  // llegaba nunca a la cuenta.
  const [iniciales, setIniciales] = useState(() => ({
    nombre: tokenInfo.nombre || '',
    apellidos: tokenInfo.apellidos || '',
    email: tokenInfo.email || '',
    telefono: tokenInfo.telefono || '',
    webs_del_cliente: tokenInfo.webs_del_cliente || '',
    idioma: tokenInfo.idioma || resolverIdioma(),
  }));

  const [pestanaActiva, setPestanaActiva] = useState('informacion');
  const [showPass, setShowPass] = useState(false);
  const [showPassRep, setShowPassRep] = useState(false);
  const [nombre, setNombre] = useState(iniciales.nombre);
  const [apellidos, setApellidos] = useState(iniciales.apellidos);
  const [email, setEmail] = useState(iniciales.email);
  const [telefono, setTelefono] = useState(iniciales.telefono);
  const [contrasena, setContrasena] = useState('');
  const [repetir, setRepetir] = useState('');
  const [webs_del_cliente, setWebs_del_cliente] = useState(iniciales.webs_del_cliente);
  // El idioma se aplica al pulsarlo (respuesta inmediata) y viaja al servidor
  // al guardar, que es lo que lo hace válido en cualquier dispositivo.
  const [idioma, setIdioma] = useState(iniciales.idioma);
  const [guardando, setGuardando] = useState(false);

  // Los errores se agrupan por pestaña: guardar Preferencias no puede quedar
  // bloqueado porque falte un apellido en Información.
  const erroresPorPestana = useMemo(() => {
    const informacion = {};
    if (!nombre.trim()) informacion.nombre = t('perfil.errores.nombre');
    if (!apellidos.trim()) informacion.apellidos = t('perfil.errores.apellidos');
    if (!email.trim()) informacion.email = t('perfil.errores.emailObligatorio');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) informacion.email = t('perfil.errores.emailInvalido');
    if (telefono.trim() && !validarTelefonoInternacional(telefono).valido) informacion.telefono = t('perfil.errores.telefonoInvalido');

    const seguridad = {};
    if (contrasena) {
      if (contrasena.length < 6) seguridad.contrasena = t('perfil.errores.contrasenaCorta');
      if (contrasena !== repetir) seguridad.repetir = t('perfil.errores.contrasenasNoCoinciden');
    } else if (repetir) {
      seguridad.contrasena = t('perfil.errores.contrasenaPrimero');
    }

    return { informacion, seguridad, preferencias: {} };
  }, [nombre, apellidos, email, telefono, contrasena, repetir, t]);

  // Los campos siguen leyendo `errors` para pintarse en rojo: la unión sirve
  // para eso, pero nunca para decidir si se puede guardar.
  const errors = useMemo(
    () => ({ ...erroresPorPestana.informacion, ...erroresPorPestana.seguridad }),
    [erroresPorPestana],
  );

  // Cambios pendientes por pestaña: alimentan el contador de cada pestaña y
  // la barra de guardado, para no perder de vista lo editado en otra sección.
  const pendientes = useMemo(() => {
    const informacion = [
      nombre !== iniciales.nombre,
      apellidos !== iniciales.apellidos,
      email !== iniciales.email,
      telefono !== iniciales.telefono,
      webs_del_cliente !== iniciales.webs_del_cliente,
    ].filter(Boolean).length;
    return {
      informacion,
      seguridad: contrasena || repetir ? 1 : 0,
      preferencias: idioma !== iniciales.idioma ? 1 : 0,
    };
  }, [nombre, apellidos, email, telefono, webs_del_cliente, contrasena, repetir, idioma, iniciales]);

  const pendientesPestana = pendientes[pestanaActiva] || 0;

  const nombreCompleto = [nombre, apellidos].filter(Boolean).join(' ') || tokenInfo.usuario;
  const esStaff = tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico';

  const pestanas = [
    { id: 'informacion', label: t('perfil.tabInformacion'), icono: FiUser, pendientes: pendientes.informacion },
    { id: 'seguridad', label: t('perfil.tabSeguridad'), icono: FiLock, pendientes: pendientes.seguridad },
    { id: 'preferencias', label: t('perfil.tabPreferencias'), icono: FiSliders, pendientes: pendientes.preferencias },
    // El servidor MCP solo responde a admin y técnicos: a un cliente la pestaña
    // no le serviría de nada, así que ni se muestra.
    ...(esStaff ? [{ id: 'integraciones', label: t('perfil.tabIntegraciones'), icono: FiCpu }] : []),
  ];
  const etiquetaRol = tokenInfo.role === 'admin'
    ? t('menu.administrador')
    : tokenInfo.role === 'tecnico' ? t('menu.tecnico') : t('gestorUsuarios.roles.user');

  // Descarta solo la pestaña visible: lo editado en otra sigue ahí, marcado
  // con su contador, en vez de desaparecer sin avisar.
  const descartarCambios = () => {
    if (pestanaActiva === 'informacion') {
      setNombre(iniciales.nombre);
      setApellidos(iniciales.apellidos);
      setEmail(iniciales.email);
      setTelefono(iniciales.telefono);
      setWebs_del_cliente(iniciales.webs_del_cliente);
    }
    if (pestanaActiva === 'seguridad') {
      setContrasena('');
      setRepetir('');
    }
    if (pestanaActiva === 'preferencias') {
      setIdioma(iniciales.idioma);
      cambiarIdioma(iniciales.idioma);
    }
  };

  const handleLogout = () => {
    cerrarSesion({ caducada: false });
  };

  /**
   * Guarda SOLO la pestaña indicada: valida su ámbito y envía sus campos. El
   * endpoint ignora las claves que no lleguen, así que un envío parcial no pisa
   * lo que se esté editando en otra pestaña.
   */
  const guardarPestana = async (pestana) => {
    const errores = erroresPorPestana[pestana] || {};
    if (Object.keys(errores).length > 0) {
      toast.error(t('perfil.revisaCampos'));
      return;
    }

    const cambiaContrasena = pestana === 'seguridad' && Boolean(contrasena);
    let datos = {};
    if (pestana === 'informacion') {
      datos = {
        nombre,
        apellidos,
        email,
        telefono: normalizarTelefonoInternacional(telefono),
        webs_del_cliente,
      };
    } else if (pestana === 'preferencias') {
      datos = { idioma };
    } else if (cambiaContrasena) {
      // La contraseña viaja en claro como 'nueva_contrasena': el hasheo lo hace
      // el servidor, igual que al crear un usuario o al resetearla.
      datos = { nueva_contrasena: contrasena };
    }

    if (Object.keys(datos).length === 0) return;

    try {
      setGuardando(true);
      const response = await axios.put(
        `${BASE_URL}/usuario/update/${tokenInfo.usuario}`,
        datos,
        { headers: { Authorization: token } }
      );
      if (response.status !== 200) {
        toast.error(t('perfil.errorGuardado'));
        return;
      }

      toast.success(t('perfil.guardado'));

      if (cambiaContrasena) {
        handleLogout();
        return;
      }

      // Lo guardado pasa a ser la nueva referencia de "sin cambios"
      setIniciales(prev => ({
        ...prev,
        ...(pestana === 'informacion' ? {
          nombre, apellidos, email,
          telefono: normalizarTelefonoInternacional(telefono),
          webs_del_cliente,
        } : {}),
        ...(pestana === 'preferencias' ? { idioma } : {}),
      }));
      if (pestana === 'informacion') setTelefono(normalizarTelefonoInternacional(telefono));

      if (response.data?.token) {
        localStorage.setItem('AuthToken', response.data.token);
        // El token nuevo ya trae el idioma guardado: la app se resincroniza
        window.dispatchEvent(new Event('auth-token-changed'));
        sincronizarIdiomaConSesion();
      }
    } catch (error) {
      console.error('Error al actualizar el perfil:', error);
      toast.error(t('perfil.errorGenerico'));
    } finally {
      setGuardando(false);
    }
  };

  const propsPanel = (id) => ({
    id: `panel-${id}`,
    role: 'tabpanel',
    'aria-labelledby': `tab-${id}`,
    className: 'perfil-panel',
    hidden: pestanaActiva !== id,
  });

  return (
    <main className="site-width perfil-page">
      <button className="boton-volver" onClick={() => navigate('/dashboard')}>
        ← {t('comun.volver')}
      </button>

      <header className="perfil-cabecera">
        <div className="perfil-avatar">
          <FotoPerfil />
        </div>
        <div className="perfil-identidad">
          <h1>{nombreCompleto}</h1>
          <p className="perfil-usuario">@{tokenInfo.usuario}</p>
          <ul className="perfil-chips">
            <li className={`perfil-chip perfil-chip--${esStaff ? tokenInfo.role : 'user'}`}>{etiquetaRol}</li>
            {email && <li className="perfil-chip">{email}</li>}
          </ul>
        </div>
        <p className="perfil-hint">{t('perfil.hint')}</p>
      </header>

      <div className="perfil-card">
        <Pestanas
          pestanas={pestanas}
          activa={pestanaActiva}
          onCambiar={setPestanaActiva}
          etiqueta={t('perfil.seccionesPerfil')}
        />

        <section {...propsPanel('informacion')}>
          <div className="perfil-panel-intro">
            <h2>{t('perfil.seccionPersonal')}</h2>
            <p>{t('perfil.seccionPersonalTexto')}</p>
          </div>

          <div className="perfil-grid">
            <div className="campo-ajuste">
              <label htmlFor="nombre">{t('comun.nombre')}</label>
              <input type="text" id="nombre" value={nombre} onChange={e => setNombre(e.target.value)} className={errors.nombre ? 'input-error' : ''} />
              {errors.nombre && <p className="campo-error">{errors.nombre}</p>}
            </div>
            <div className="campo-ajuste">
              <label htmlFor="apellidos">{t('comun.apellidos')}</label>
              <input type="text" id="apellidos" value={apellidos} onChange={e => setApellidos(e.target.value)} className={errors.apellidos ? 'input-error' : ''} />
              {errors.apellidos && <p className="campo-error">{errors.apellidos}</p>}
            </div>
            <div className="campo-ajuste">
              <label htmlFor="email">{t('comun.email')}</label>
              <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className={errors.email ? 'input-error' : ''} />
              {errors.email && <p className="campo-error">{errors.email}</p>}
            </div>
            <div className="campo-ajuste">
              <label htmlFor="telefono">{t('comun.telefono')}</label>
              <input type="tel" inputMode="tel" autoComplete="tel" id="telefono" placeholder={t('perfilUsuario.telefonoInternacionalPlaceholder')} value={telefono} onChange={e => setTelefono(e.target.value)} onBlur={() => setTelefono(normalizarTelefonoInternacional(telefono))} className={errors.telefono ? 'input-error' : ''} aria-describedby="telefono-ayuda" />
              <p className="campo-ayuda" id="telefono-ayuda">{t('perfilUsuario.telefonoInternacionalHint')}</p>
              {errors.telefono && <p className="campo-error">{errors.telefono}</p>}
            </div>
            <div className="campo-ajuste perfil-campo-ancho">
              <label htmlFor="webs_del_cliente">{t('perfil.sitiosMantenimiento')}</label>
              <input type="text" id="webs_del_cliente" value={webs_del_cliente} onChange={e => setWebs_del_cliente(e.target.value)} aria-describedby="webs-ayuda" />
              <p className="campo-ayuda" id="webs-ayuda">{t('perfil.sitiosMantenimientoHint')}</p>
            </div>
          </div>
        </section>

        <section {...propsPanel('seguridad')}>
          <div className="perfil-panel-intro">
            <h2>{t('perfil.seccionSeguridad')}</h2>
            <p>{t('perfil.seccionSeguridadTexto')}</p>
          </div>

          <div className="perfil-grid">
            <div className="campo-ajuste perfil-campo-ancho">
              <label htmlFor="usuario">{t('comun.usuario')}</label>
              <input type="text" name="usuario" id="usuario" value={`@${tokenInfo.usuario}`} disabled />
              <p className="campo-ayuda">
                <Trans i18nKey="perfil.problemasUsuario">
                  ¿Problemas con tu usuario? Llámanos al <a href="tel:+34856901276">856 901 276</a> ext. #2.
                </Trans>
              </p>
            </div>

            <div className="campo-ajuste">
              <label htmlFor="contrasena">{t('comun.contrasena')}</label>
              <div className={`input-with-toggle ${errors.contrasena ? 'input-error' : ''}`}>
                <input
                  type={showPass ? 'text' : 'password'}
                  id="contrasena"
                  value={contrasena}
                  onChange={e => setContrasena(e.target.value)}
                  autoComplete="new-password"
                  placeholder={t('perfil.placeholderContrasena')}
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowPass(v => !v)}
                  aria-label={showPass ? t('comun.ocultarContrasena') : t('comun.mostrarContrasena')}
                  aria-pressed={showPass}
                  title={showPass ? t('comun.ocultarContrasena') : t('comun.mostrarContrasena')}
                >
                  {showPass ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {errors.contrasena && <p className="campo-error">{errors.contrasena}</p>}
            </div>

            <div className="campo-ajuste">
              <label htmlFor="repetir-contrasena">{t('perfil.repetirContrasena')}</label>
              <div className={`input-with-toggle ${errors.repetir ? 'input-error' : ''}`}>
                <input
                  type={showPassRep ? 'text' : 'password'}
                  id="repetir-contrasena"
                  value={repetir}
                  onChange={e => setRepetir(e.target.value)}
                  autoComplete="new-password"
                  placeholder={t('perfil.placeholderRepetir')}
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowPassRep(v => !v)}
                  aria-label={showPassRep ? t('comun.ocultarContrasena') : t('comun.mostrarContrasena')}
                  aria-pressed={showPassRep}
                  title={showPassRep ? t('comun.ocultarContrasena') : t('comun.mostrarContrasena')}
                >
                  {showPassRep ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {errors.repetir && <p className="campo-error">{errors.repetir}</p>}
            </div>
          </div>

          <div className="adhoc-warning">
            <p>{t('perfil.avisoContrasena')}</p>
          </div>
        </section>

        <section {...propsPanel('preferencias')}>
          <div className="perfil-panel-intro">
            <h2>{t('perfil.seccionPreferencias')}</h2>
            <p>{t('perfil.seccionPreferenciasTexto')}</p>
          </div>

          <div className="campo-ajuste perfil-campo-ancho">
            <label>{t('idioma.etiqueta')}</label>
            <SelectorIdioma variante="campo" value={idioma} onCambio={setIdioma} />
            <p className="campo-ayuda">{t('idioma.ayudaPerfil')}</p>
          </div>
        </section>

        {esStaff && (
          <section {...propsPanel('integraciones')}>
            <div className="perfil-panel-intro">
              <h2>{t('perfil.seccionIntegraciones')}</h2>
              <p>{t('perfil.seccionIntegracionesTexto')}</p>
            </div>
            <IntegracionesIA onAviso={(tipo, texto) => (tipo === 'error' ? toast.error(texto) : toast.success(texto))} />
          </section>
        )}
      </div>

      {/* Barra de guardado: solo aparece con cambios, y sigue visible aunque
          se cambie de pestaña, para no dejar ediciones olvidadas detrás. */}
      {pendientesPestana > 0 && (
        <div className="perfil-barra-guardado" role="status">
          <span className="perfil-barra-texto">
            {t('perfil.cambiosSinGuardarEn', {
              count: pendientesPestana,
              seccion: pestanas.find(p => p.id === pestanaActiva)?.label || '',
            })}
          </span>
          <div className="perfil-barra-acciones">
            <button type="button" className="perfil-boton-descartar" onClick={descartarCambios} disabled={guardando}>
              {t('perfil.descartar')}
            </button>
            <button
              type="button"
              className="boton-primario"
              onClick={() => guardarPestana(pestanaActiva)}
              disabled={guardando || Object.keys(erroresPorPestana[pestanaActiva] || {}).length > 0}
            >
              {guardando ? t('perfil.guardando') : t('perfil.guardarSeccion')}
            </button>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} />
    </main>
  );
};

export default Perfil;
